/**
 * The Funrooms.
 *
 * A soft-play centre the size of a level. Not a building with play equipment in
 * it — the equipment *is* the building, so the plan is drawn out of the same
 * vocabulary a frame is: bays, tubes, junctions and dead ends.
 *
 * The level is a field of **activity bays**, each one an octagon rather than a
 * room, laid out on a loose module grid with several metres of solid building
 * between every pair of them. Nothing shares a wall and nothing has a door in
 * the ordinary sense. Every bay is reached through a **tube** — a single-cell
 * run cut through the rock between two bays, dog-legged so you cannot see one
 * bay from the next, with a threshold at each mouth. Half of them are carved as
 * `Cell.Doorway` rather than `Cell.Floor`, which is what makes this level read
 * the way it does: the renderer heads a doorway at 2.15 m, so a run of them is a
 * continuous low tunnel with a rib at every 3.4 m seam under a building whose
 * rooms are 4.2 m tall. Walking one, you duck.
 *
 * Off the tubes and the bay walls hang **crawls**: low spurs that go two to six
 * cells into the building and stop, usually in a small pocket. They lead nowhere
 * on purpose, and the supply pass hunts for a pocket, so crawling into one is
 * occasionally worth it.
 *
 * Inside a bay the fit-out is more equipment. Some carry an **island** — a solid
 * octagonal block you circulate around, three cells of floor clear on every side
 * so the play frame can get past it. Some carry a ring of **padded posts**. Some
 * carry a **walkway**: a two-cell gallery down one long side, cut off from the
 * play floor by a wall with an opening every third cell, open at both ends. That
 * wall is the only long run of blank wall the level has, which is what a bank of
 * arcade cabinets and a slide both want, and the openings are what stop it
 * reading as a corridor rather than as something you look down onto the floor
 * from.
 *
 * Two rules hold across all of it. **Every bay is reachable**: the tubes are a
 * spanning tree over the bays before any of the optional loops are added, so
 * connectivity is a property of the plan rather than something the repair pass
 * is left to find. And **nothing may plug a tube**: they are one cell wide and
 * they are the only way through, which is why `pillarsInDoorways` is switched
 * off here and why the crawls are dug into rock that has been checked clear on
 * both sides.
 *
 * What the *grid* cannot do is go up: it is one storey, `ceilingHeight` is one
 * number for the whole level, and no `LevelSpec` can ask for a bay twice as tall
 * as the tube you came in through. The height this level has therefore comes
 * from two places instead. The low tunnels are one, and they are a side effect
 * of how the renderer heads a doorway. The other is the fit-out below, which
 * builds the whole first floor out of props: a frame module's deck is a slab at
 * 2.25 m, and a block of them is a storey the grid knows nothing about and the
 * player walks under.
 *
 * Which is the second half of this file. `generateFunrooms` carves the plan;
 * `dressFunrooms` fits it out, after the props pass, and is what makes a bay
 * read as the inside of a play frame rather than as a hall with one standing in
 * it. It lives here rather than in `props.ts` for the same reason `dressMotion`
 * does: everything it knows is about this level — where a bay is, which patch of
 * it is the ball pool, which face of a column the netting goes on — and the
 * general placer's vocabulary is pockets of scenery against a wall.
 */

import { TUNING } from '../../core/config';
import { clamp } from '../../core/math';
import {
  Cell,
  type ExitPoint,
  type Grid,
  type ItemSpot,
  type PropKind,
  type PropSpot,
  type RNG,
  type Vec2,
} from '../../core/types';
import { PROP_DEFS } from './propTables';
import { addRegion, fillRect, inset, interiorRect, type GenSite, type Rect } from './site';
import { applyWrongness } from './wrongness';

/**
 * Module sizes in cells, at 3.4 m a cell.
 *
 * A module is the plot a bay is set out in, not the bay itself. Sixty to ninety
 * metres, which is well past the sixty-metre fog: you never see a whole bay, and
 * you never see two.
 */
const MODULE: readonly [number, number] = [18, 27];
/** Cells of solid building left between a bay and each edge of its module. */
const MARGIN: readonly [number, number] = [2, 4];
/** Cells taken off each corner of a bay, at 45°. */
const CHAMFER: readonly [number, number] = [2, 5];
/** Modules left as solid building, so the grid is not a grid. */
const SKIP_CHANCE = 0.12;
/** Neighbouring bays joined by a tube beyond the ones the spanning tree needs. */
const LOOP_CHANCE = 0.45;
/** Tubes carved low — `Cell.Doorway`, headed at 2.15 m — rather than full height. */
const LOW_CHANCE = 0.5;
/** Depth of a walkway gallery inside a bay, in cells. */
const WALK_DEPTH = 2;
/** Cells between the openings in a walkway's inner wall. */
const WALK_WINDOW = 3;
/** Cells per activity zone on a play floor. About thirty metres square. */
const ZONE_CELLS = 90;
/** Smallest bay worth carving, per side. */
const MIN_BAY = 9;

/** One activity bay: an octagonal plate, and how hard its corners were cut. */
interface Bay {
  rect: Rect;
  /** Cells cut off each corner at 45°. Also the inset a mouth must keep. */
  cut: number;
}

/**
 * The bays, kept for the fit-out pass.
 *
 * `dressFunrooms` cannot run from `generateFunrooms` — it places props, and
 * props are placed against a finished, populated, repaired grid — but by then
 * the plan is gone: a bay is not a region, it is several, and nothing
 * downstream can tell the octagon it was carved as from the tunnels that meet
 * it. Keyed on the grid rather than held in a module variable so two levels
 * generated in the same session cannot see each other's plan.
 */
const PLANS = new WeakMap<Grid, readonly Bay[]>();

/** A run in tube coordinates: `u` along the tube's main axis, `v` across it. */
interface Run {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}

/** Carve the Funrooms profile into `site`. */
export function generateFunrooms(site: GenSite): void {
  const { grid, rng } = site;
  const bounds = interiorRect(grid);

  const cols = bands(site, bounds.x, bounds.x + bounds.w - 1);
  const rows = bands(site, bounds.y, bounds.y + bounds.h - 1);

  // The bay grid, with holes in it. `at` is module -> bay index, so the loop
  // pass can ask "is there a bay next door" without searching.
  const bays: Bay[] = [];
  const at = new Int32Array(cols.length * rows.length).fill(-1);
  for (let j = 0; j < rows.length; j++) {
    for (let i = 0; i < cols.length; i++) {
      if (rng.chance(SKIP_CHANCE)) continue;
      const bay = carveBay(site, cols[i]!, rows[j]!);
      if (!bay) continue;
      at[j * cols.length + i] = bays.length;
      bays.push(bay);
    }
  }
  // Every module skipped is a level with no floor in it. It takes a couple of
  // dozen coin flips and nothing downstream survives it, so it gets a floor.
  if (bays.length === 0) {
    const bay = carveBay(site, cols[cols.length >> 1]!, rows[rows.length >> 1]!, true);
    if (!bay) return;
    at[(rows.length >> 1) * cols.length + (cols.length >> 1)] = 0;
    bays.push(bay);
  }

  // Fit-out before labelling, because a walkway is carved out of a bay and has
  // to win the cells it took. `buildRegionIndex` lets a later region beat an
  // earlier one, so the order here is zoning first, walkways second, tubes last.
  const walks: Rect[] = [];
  for (const bay of bays) {
    const walk = fitOut(site, bay);
    if (walk) walks.push(walk);
  }
  for (const bay of bays) zones(site, bay);
  for (const walk of walks) addRegion(site, walk, 'hall');

  const legs: Rect[] = [];
  plumb(site, bays, at, cols.length, legs);
  crawls(site, bays, legs, Math.round(bays.length * 0.9));

  applyWrongness(site, {
    innerRooms: [1, 2],
    doorsToNothing: [4, 8],
    // Nearly none. A perforated wall needs a wall cell with floor on both sides
    // of it, and on this level almost the only place that describes is the strip
    // of building between a tube and the bay it is running past — where one
    // would turn the tube into a colonnade and undo the point of it.
    perforatedWalls: [0, 1],
    wallFins: [5, 9],
    // Zero, and not as a matter of taste: `pillarsInDoorways` collects every
    // `Cell.Doorway` on the level, and on this one the tunnels *are* doorways. A
    // column dropped into a 3.4 m tube is a plug in the only way through.
    pillarsInDoorways: [0, 0],
    // The bays are the level and their middles are spoken for. A patch of
    // columns at every other cell is the one wrongness that eats open floor.
    tightPillarPatches: [0, 0],
    // A block set one cell inside a bay turns the bay into a ring corridor,
    // which on a forty-metre plate is most of a venue turned to rock.
    ringRooms: false,
    // Bays are twenty cells across; the two-cell fin that reads in an office is
    // invisible standing in one.
    finLength: [3, 6],
  });

  PLANS.set(grid, bays);
}

/* ------------------------------------------------------------------ *
 * The bay grid
 * ------------------------------------------------------------------ */

/** Contiguous modules covering [from, to]. The gaps come from the bay margins. */
function bands(site: GenSite, from: number, to: number): Array<{ a: number; b: number }> {
  const { rng } = site;
  const out: Array<{ a: number; b: number }> = [];
  let a = from;
  while (a <= to) {
    const remaining = to - a + 1;
    if (remaining < MODULE[0] * 2) {
      // Absorb the tail rather than leaving a module too small to hold a bay.
      if (out.length > 0 && remaining < MODULE[0]) out[out.length - 1]!.b = to;
      else out.push({ a, b: to });
      break;
    }
    const size = Math.min(rng.int(MODULE[0], MODULE[1]), remaining);
    out.push({ a, b: a + size - 1 });
    a += size;
  }
  return out;
}

/**
 * One bay: an octagonal plate somewhere inside its module.
 *
 * The four margins are drawn separately and one axis is often shortened
 * further, so a bay sits off-centre in its plot. That is what varies the gaps —
 * two or three cells on one side of a bay and eight on the other — and the wide
 * gaps are where the junction pods and the crawls end up fitting.
 */
function carveBay(
  site: GenSite,
  col: { a: number; b: number },
  row: { a: number; b: number },
  full = false
): Bay | null {
  const { grid, rng } = site;
  const mw = col.b - col.a + 1;
  const mh = row.b - row.a + 1;
  const left = full ? MARGIN[0] : rng.int(MARGIN[0], MARGIN[1]);
  const right = full ? MARGIN[0] : rng.int(MARGIN[0], MARGIN[1]);
  const top = full ? MARGIN[0] : rng.int(MARGIN[0], MARGIN[1]);
  const bottom = full ? MARGIN[0] : rng.int(MARGIN[0], MARGIN[1]);

  // One bay in five is a party room rather than a play floor: the same module
  // with most of it left as building. Clamped so the short axis never drops
  // under a bay worth having.
  const alongX = rng.chance(0.5);
  const room = alongX ? mw - left - right : mh - top - bottom;
  const shrink = full || !rng.chance(0.22) ? 0 : Math.max(0, Math.min(rng.int(3, 7), room - MIN_BAY));

  const rect: Rect = {
    x: col.a + left + (alongX ? shrink : 0),
    y: row.a + top + (alongX ? 0 : shrink),
    w: mw - left - right - (alongX ? shrink : 0),
    h: mh - top - bottom - (alongX ? 0 : shrink),
  };
  if (rect.w < MIN_BAY || rect.h < MIN_BAY) return null;

  // Two cells off a corner is a chamfer, five is most of a circle. Held to less
  // than half the short side so the two chamfers never meet in the middle.
  const cut = clamp(rng.int(CHAMFER[0], CHAMFER[1]), 1, (Math.min(rect.w, rect.h) >> 1) - 2);
  chamfer(grid, rect, cut, Cell.Floor);
  return { rect, cut };
}

/**
 * A rectangle with its corners taken off at 45°.
 *
 * At 3.4 m a cell a stair-stepped arc and a straight chamfer are the same
 * drawing, and the chamfer is the one somebody could have built: soft-play ball
 * pools and toddler pens really are octagons. Used with `Cell.Floor` it carves a
 * bay; with `Cell.Wall` it builds an island.
 */
function chamfer(grid: Grid, r: Rect, cut: number, cell: Cell): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      if (Math.min(x, r.w - 1 - x) + Math.min(y, r.h - 1 - y) < cut) continue;
      grid.set(r.x + x, r.y + y, cell);
    }
  }
}

/* ------------------------------------------------------------------ *
 * What is inside a bay
 * ------------------------------------------------------------------ */

/**
 * The structure built into a bay, if any. Returns the walkway strip so the
 * caller can label it after the bay's own zoning has gone in.
 *
 * About half of the big bays get nothing, and that is still the intended answer
 * rather than the leftover one — but for a different reason than it once was.
 * It used to be that the equipment was four very wide solid props that needed a
 * clear floor to stand on. The fit-out no longer works that way: it fills the
 * bay whatever is in it. What an island or a ring of posts buys now is a bay
 * whose *plan* is not another octagon — something for the frame to be built
 * around and for the ball pool to stop against.
 */
function fitOut(site: GenSite, bay: Bay): Rect | null {
  const { rng } = site;
  const area = bay.rect.w * bay.rect.h;
  if (area < 120) return null;

  const roll = rng.next();
  if (roll < 0.22) return walkway(site, bay);
  if (roll < 0.38 && area >= 200) {
    island(site, bay);
    return null;
  }
  if (roll < 0.52) posts(site, bay);
  return null;
}

/**
 * A solid octagonal block in the middle of a bay.
 *
 * The thing at the centre of a play frame that you go round rather than through.
 * Three cells of floor are held clear on every side of it, which is not a
 * margin for looks: the play structure is three metres across and the ball pit
 * three and a half, and a ring narrower than that is a bay with nowhere left to
 * put the equipment it exists for.
 */
function island(site: GenSite, bay: Bay): void {
  const { grid, rng } = site;
  const r = bay.rect;
  const w = clamp(Math.round(r.w * 0.3), 3, 9);
  const h = clamp(Math.round(r.h * 0.3), 3, 9);
  const box: Rect = {
    x: r.x + ((r.w - w) >> 1) + rng.int(-1, 1),
    y: r.y + ((r.h - h) >> 1) + rng.int(-1, 1),
    w,
    h,
  };
  if (!allFloor(grid, inset(box, -3))) return;
  chamfer(grid, box, Math.min(2, Math.min(w, h) >> 1), Cell.Wall);
}

/**
 * Padded posts on a ring inside a bay.
 *
 * A `Cell.Pillar` is passable — three tenths of a cell clear either side — so a
 * ring of them is a boundary you can see through and step between rather than a
 * wall, which is exactly what the posts round a play area are. Held clear of the
 * chamfer so the ring reads as concentric with the bay.
 */
function posts(site: GenSite, bay: Bay): void {
  const { grid, rng } = site;
  const r = bay.rect;
  // A square ring inside an octagon, held one cell clear of the chamfer. A bay
  // whose corners have been cut hard enough has no middle left to ring.
  const room = Math.min(r.w, r.h) - 2 * (bay.cut + 1);
  if (room < 5) return;
  const side = clamp(Math.round(Math.min(r.w, r.h) * 0.52), 5, room);

  const x0 = r.x + ((r.w - side) >> 1);
  const y0 = r.y + ((r.h - side) >> 1);
  const step = rng.int(2, 3);
  for (let x = x0; x < x0 + side; x += step) {
    post(grid, x, y0);
    post(grid, x, y0 + side - 1);
  }
  for (let y = y0 + step; y < y0 + side - 1; y += step) {
    post(grid, x0, y);
    post(grid, x0 + side - 1, y);
  }
}

function post(grid: Grid, x: number, y: number): void {
  if (grid.at(x, y) === Cell.Floor) grid.set(x, y, Cell.Pillar);
}

/**
 * A walkway down one long side of a bay: two cells deep, walled off from the
 * play floor, open at both ends, with an opening every third cell.
 *
 * The wall is the point twice over. It is the only long run of blank building
 * on the level — everything else is either an octagon or one cell wide — and a
 * bank of arcade cabinets and the mouth of a slide both need ten metres of it
 * with clear floor in front. And the openings are what keep it from reading as a
 * corridor: each one is headed at 2.15 m by the renderer, so walking the strip
 * you look out through a run of frames onto the floor you are not on.
 *
 * It cannot seal anything: the wall stops short of both chamfers, so the strip
 * is open to the bay at both ends before the openings are counted at all.
 */
function walkway(site: GenSite, bay: Bay): Rect | null {
  const { grid, rng } = site;
  const r = bay.rect;
  const alongX = r.w >= r.h;
  const long = alongX ? r.w : r.h;
  const short = alongX ? r.h : r.w;
  // Depth for the strip, its wall, and enough left over that the play side is
  // still a play floor rather than a second corridor.
  if (short < WALK_DEPTH + 7 || long < bay.cut * 2 + 10) return null;

  const near = rng.chance(0.5);
  const wallAt = near ? WALK_DEPTH : short - 1 - WALK_DEPTH;
  const stripAt = near ? 0 : short - WALK_DEPTH;
  const from = bay.cut + 2;
  const to = long - 1 - bay.cut - 2;

  const mark = (t: number, k: number, cell: Cell): void => {
    if (alongX) grid.set(r.x + t, r.y + k, cell);
    else grid.set(r.x + k, r.y + t, cell);
  };
  for (let t = from; t <= to; t++) {
    const window = (t - from) % WALK_WINDOW === WALK_WINDOW - 1;
    mark(t, wallAt, window ? Cell.Doorway : Cell.Wall);
  }

  return alongX
    ? { x: r.x, y: r.y + stripAt, w: r.w, h: WALK_DEPTH }
    : { x: r.x + stripAt, y: r.y, w: WALK_DEPTH, h: r.h };
}

/**
 * Label a bay as the activity zones it is set out in.
 *
 * Regions here are labels rather than walls, and labelling is the only lever the
 * layout has over what ends up in a room. The placer draws its large free-
 * standing objects per region — one draw plus a share of the region's area — and
 * spaces them apart *within* a region, so a single seventy-metre bay comes out
 * holding about as much as a thirty-metre one, which on this level is the wrong
 * answer: the equipment is the room.
 *
 * Zoning a bay into strips of about thirty metres square earns a draw each and
 * spaces them independently, which is how the frame ends up at one end and the
 * pit at the other. It is also how a soft-play centre is laid out — toddler
 * area, main frame, ball pool — so the label describes the building rather than
 * working around the placer.
 */
function zones(site: GenSite, bay: Bay): void {
  const r = bay.rect;
  const area = r.w * r.h;
  if (area < 150) {
    addRegion(site, r, area >= 100 ? 'atrium' : 'room');
    return;
  }
  const alongX = r.w >= r.h;
  const long = alongX ? r.w : r.h;
  const short = alongX ? r.h : r.w;
  // At least two, and never so many that a zone is narrower than it is deep.
  const n = Math.max(2, Math.min(Math.round(area / ZONE_CELLS), Math.floor(long / Math.max(5, short / 2))));
  for (let i = 0; i < n; i++) {
    const a = Math.round((long * i) / n);
    const b = Math.round((long * (i + 1)) / n);
    addRegion(
      site,
      alongX ? { x: r.x + a, y: r.y, w: b - a, h: r.h } : { x: r.x, y: r.y + a, w: r.w, h: b - a },
      'atrium'
    );
  }
}

/* ------------------------------------------------------------------ *
 * Tubes
 * ------------------------------------------------------------------ */

/**
 * Join the bays up.
 *
 * A minimum spanning tree first, over every pair of bays, so the level is
 * connected by construction rather than by the repair pass — a tunnel driven
 * through solid rock by `repairConnectivity` is a scar, and on a plan made
 * almost entirely of rock there would be a lot of them. Prim's over the complete
 * graph with squared-distance weights picks near neighbours on its own, so the
 * tree is short tubes between adjacent plots.
 *
 * Then the loops: about half the remaining side-by-side pairs, which is what
 * turns a tree you can only ever backtrack out of into a plan with more than one
 * way round. Adjacency here is module adjacency, so a loop is always a short
 * tube and never a service run across the map.
 *
 * `legs` collects the straight sections, which is what the crawl pass hangs off.
 */
function plumb(site: GenSite, bays: readonly Bay[], at: Int32Array, cols: number, legs: Rect[]): void {
  const { rng } = site;
  const n = bays.length;
  if (n === 0) return;

  const mid = (r: Rect): { x: number; y: number } => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
  const centres = bays.map((b) => mid(b.rect));
  const far = (a: number, b: number): number => {
    const dx = centres[a]!.x - centres[b]!.x;
    const dy = centres[a]!.y - centres[b]!.y;
    return dx * dx + dy * dy;
  };

  const done = new Set<number>();
  const join = (a: number, b: number): void => {
    const key = a < b ? a * n + b : b * n + a;
    if (done.has(key)) return;
    done.add(key);
    tube(site, bays[a]!, bays[b]!, legs);
  };

  // Prim's. No dice anywhere in it, and ties break by index, so the spine is a
  // function of the bay positions alone.
  const inTree = new Uint8Array(n);
  const best = new Float64Array(n).fill(Infinity);
  const from = new Int32Array(n).fill(0);
  inTree[0] = 1;
  for (let k = 1; k < n; k++) best[k] = far(0, k);
  for (let step = 1; step < n; step++) {
    let pick = -1;
    for (let k = 0; k < n; k++) {
      if (inTree[k]) continue;
      if (pick < 0 || best[k]! < best[pick]!) pick = k;
    }
    if (pick < 0) break;
    inTree[pick] = 1;
    join(from[pick]!, pick);
    for (let k = 0; k < n; k++) {
      if (inTree[k]) continue;
      const d = far(pick, k);
      if (d < best[k]!) {
        best[k] = d;
        from[k] = pick;
      }
    }
  }

  const rows = at.length / cols;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const here = at[j * cols + i]!;
      if (here < 0) continue;
      const east = i + 1 < cols ? at[j * cols + i + 1]! : -1;
      const south = j + 1 < rows ? at[(j + 1) * cols + i]! : -1;
      if (east >= 0 && rng.chance(LOOP_CHANCE)) join(here, east);
      if (south >= 0 && rng.chance(LOOP_CHANCE)) join(here, south);
    }
  }
}

/**
 * One tube between two bays: a mouth in each, and a dog-leg through the rock.
 *
 * The bend is not decoration. A straight tube between two bays is a sight line
 * from one to the other, and a level whose bays are all visible from each other
 * is the Lobby with rounder corners. Bent, a tube shows you a wall four metres
 * in and nothing else until you are through it.
 *
 * The knees are carved full height whatever the tube is, which turns a renderer
 * detail into a piece of plan: a headed doorway needs solid on both sides, so a
 * corner would lose its header for one cell and the ceiling would jump. Made
 * deliberate, that cell is the junction you stand up in, and where there is rock
 * enough it is widened into a pod.
 */
function tube(site: GenSite, a: Bay, b: Bay, legs: Rect[]): void {
  const { grid, rng } = site;

  // Route along whichever axis actually separates the two bays. Two bays in
  // different modules are always disjoint on at least one axis by at least the
  // two margins between them, so the larger gap is never zero.
  const gapX = clearance(a.rect.x, a.rect.x + a.rect.w - 1, b.rect.x, b.rect.x + b.rect.w - 1);
  const gapY = clearance(a.rect.y, a.rect.y + a.rect.h - 1, b.rect.y, b.rect.y + b.rect.h - 1);
  const alongX = gapX >= gapY;

  const A = toRun(a.rect, alongX);
  const B = toRun(b.rect, alongX);
  const fwd = B.u0 + B.u1 >= A.u0 + A.u1 ? 1 : -1;
  const aFace = fwd > 0 ? A.u1 + 1 : A.u0 - 1;
  const bFace = fwd > 0 ? B.u0 - 1 : B.u1 + 1;
  const pu = aFace + fwd;
  const qu = bFace - fwd;
  const lo = Math.min(pu, qu);
  const hi = Math.max(pu, qu);
  if (hi < lo) return;

  // A mouth has to open onto floor, and the chamfer means the last `cut` cells
  // of a face are not floor. Aimed at the other bay's middle and then pushed off
  // it: two mouths that line up give a tube with no bend worth having, and the
  // bend is the whole reason you cannot see one bay from the next.
  const drift = (r: Run, cut: number, aim: number): number => {
    const lo = r.v0 + cut;
    const hi = r.v1 - cut;
    return clamp(aim + Math.round(rng.range(-0.35, 0.35) * (hi - lo)), lo, hi);
  };
  const av = drift(A, a.cut, Math.round((B.v0 + B.v1) / 2));
  const bv = drift(B, b.cut, Math.round((A.v0 + A.v1) / 2));

  let carved: Run[] = [];
  let knee = -1;
  // Both pods are decided here, while the ground is still rock. Asking after the
  // legs are carved would always answer no: a leg runs through its own knee.
  const pods: Rect[] = [];
  if (av === bv) {
    carved = [{ u0: lo, u1: hi, v0: av, v1: av }];
  } else {
    // Turn as near the middle of the gap as the rock allows, working outward.
    let clean = false;
    for (const m of outward(lo, hi)) {
      const legsUV = dogleg(pu, qu, av, bv, m);
      if (!legsUV.every((r) => isRock(grid, toRect(r, alongX)))) continue;
      carved = legsUV;
      knee = m;
      clean = true;
      break;
    }
    if (!clean) {
      // Nowhere clean to turn. Carve it anyway: the two bays being joined at all
      // matters more than the tube passing through something on the way.
      knee = (lo + hi) >> 1;
      carved = dogleg(pu, qu, av, bv, knee);
    } else {
      for (const v of [av, bv]) {
        const rect = toRect({ u0: knee - 1, u1: knee + 1, v0: v - 1, v1: v + 1 }, alongX);
        if (isRock(grid, rect)) pods.push(rect);
      }
    }
  }

  const low = rng.chance(LOW_CHANCE);
  for (const r of carved) fillRect(grid, toRect(r, alongX), low ? Cell.Doorway : Cell.Floor);
  for (const rect of pods) fillRect(grid, rect, Cell.Floor);
  // A bend is always full height, pod or not: a headed doorway needs solid on
  // both sides, so the corner cell would drop its header anyway. Carved as
  // floor, that stops being a seam in the ceiling and starts being a junction.
  if (knee >= 0) {
    set(grid, knee, av, alongX, Cell.Floor);
    set(grid, knee, bv, alongX, Cell.Floor);
  }
  // The mouths last, so nothing above has overwritten a threshold.
  set(grid, aFace, av, alongX, Cell.Doorway);
  set(grid, bFace, bv, alongX, Cell.Doorway);

  for (const r of carved) {
    const rect = toRect(r, alongX);
    addRegion(site, rect, 'hall');
    if (rect.w >= 4 || rect.h >= 4) legs.push(rect);
  }
  // After the legs, so the pod keeps its own label where the two overlap.
  for (const rect of pods) addRegion(site, rect, 'room');
}

/** The three straight sections of a dog-leg turning at `m`, in tube coordinates. */
function dogleg(pu: number, qu: number, av: number, bv: number, m: number): Run[] {
  return [
    { u0: Math.min(pu, m), u1: Math.max(pu, m), v0: av, v1: av },
    { u0: m, u1: m, v0: Math.min(av, bv), v1: Math.max(av, bv) },
    { u0: Math.min(m, qu), u1: Math.max(m, qu), v0: bv, v1: bv },
  ];
}

/** `lo..hi`, middle first and alternating outward. */
function outward(lo: number, hi: number): number[] {
  const out: number[] = [];
  const c = (lo + hi) >> 1;
  for (let k = 0; k <= (hi - lo) * 2; k++) {
    const m = c + (k % 2 === 0 ? k >> 1 : -((k + 1) >> 1));
    if (m >= lo && m <= hi && !out.includes(m)) out.push(m);
  }
  return out;
}

/** Cells of daylight between two closed intervals, or 0 where they overlap. */
function clearance(a0: number, a1: number, b0: number, b1: number): number {
  if (b0 > a1) return b0 - a1 - 1;
  if (a0 > b1) return a0 - b1 - 1;
  return 0;
}

/** Tube coordinates to grid coordinates. */
function toRect(run: Run, alongX: boolean): Rect {
  return alongX
    ? { x: run.u0, y: run.v0, w: run.u1 - run.u0 + 1, h: run.v1 - run.v0 + 1 }
    : { x: run.v0, y: run.u0, w: run.v1 - run.v0 + 1, h: run.u1 - run.u0 + 1 };
}

/** A bay's extent in the tube coordinates of one axis. */
function toRun(r: Rect, alongX: boolean): Run {
  return alongX
    ? { u0: r.x, u1: r.x + r.w - 1, v0: r.y, v1: r.y + r.h - 1 }
    : { u0: r.y, u1: r.y + r.h - 1, v0: r.x, v1: r.x + r.w - 1 };
}

function set(grid: Grid, u: number, v: number, alongX: boolean, cell: Cell): void {
  if (alongX) grid.set(u, v, cell);
  else grid.set(v, u, cell);
}

/* ------------------------------------------------------------------ *
 * Crawls
 * ------------------------------------------------------------------ */

/** Direction order used by the crawl pass: +X, -X, +Y, -Y. */
const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/**
 * Short low spurs that go into the building and stop.
 *
 * Half of what makes a play frame a play frame is the parts of it that do not go
 * anywhere: you commit to a tunnel, it turns out to be four metres long, and you
 * come back out. They are dug into checked rock with a cell of it left on both
 * sides, so a crawl is always a dead end rather than a second way round, and the
 * run itself is `Cell.Doorway` — headed, ribbed, and low enough that going in is
 * a decision.
 *
 * The pocket at the end is `Cell.Floor` and labelled `closet`, which is the one
 * region kind the supply pass actively hunts for. That is the whole reward loop:
 * the tunnel that leads nowhere is where the sweets are.
 */
function crawls(site: GenSite, bays: readonly Bay[], legs: readonly Rect[], want: number): void {
  const { rng } = site;
  if (want <= 0) return;
  let done = 0;
  for (let tries = 0; tries < want * 8 && done < want; tries++) {
    // Off a tube where there is one, off a bay wall otherwise. Tubes first
    // because a crawl off a crawl is the shape this is trying to make.
    const fromLeg = legs.length > 0 && rng.chance(0.6);
    const seed = fromLeg ? legSeed(site, rng.pick(legs)) : baySeed(site, rng.pick(bays));
    if (!seed) continue;
    if (dig(site, seed.x, seed.y, seed.d)) done++;
  }
}

/** A cell on a tube and a direction square out of it. */
function legSeed(site: GenSite, leg: Rect): { x: number; y: number; d: number } | null {
  const { rng } = site;
  const alongX = leg.w >= leg.h;
  const len = alongX ? leg.w : leg.h;
  if (len < 4) return null;
  // Never the two end cells: a crawl off the last cell of a leg is a crawl off
  // the knee, and the knee is already a junction.
  const t = rng.int(1, len - 2);
  const x = alongX ? leg.x + t : leg.x;
  const y = alongX ? leg.y : leg.y + t;
  return { x, y, d: (alongX ? 2 : 0) + (rng.chance(0.5) ? 0 : 1) };
}

/** A cell on a bay's face, clear of both chamfers, and the way out of it. */
function baySeed(site: GenSite, bay: Bay): { x: number; y: number; d: number } | null {
  const { rng } = site;
  const r = bay.rect;
  const d = rng.int(0, 3);
  const alongX = d < 2;
  const long = alongX ? r.h : r.w;
  const first = bay.cut + 2;
  if (long - 1 - bay.cut - 2 < first) return null;
  const t = rng.int(first, long - 1 - bay.cut - 2);
  if (alongX) return { x: d === 0 ? r.x + r.w - 1 : r.x, y: r.y + t, d };
  return { x: r.x + t, y: d === 2 ? r.y + r.h - 1 : r.y, d };
}

/**
 * Dig one crawl out of `(sx, sy)` heading `d`. Returns false when the rock is
 * not clear, which is most of the time and is the point: a crawl only exists
 * where the building is thick enough to hide one.
 */
function dig(site: GenSite, sx: number, sy: number, d: number): boolean {
  const { grid, rng } = site;
  // A crawl that does not start on something walkable is a stranded pocket for
  // the repair pass to tunnel to, which is the one thing this file is avoiding.
  if (!grid.isWalkable(sx, sy)) return false;
  const dx = DX[d]!;
  const dy = DY[d]!;
  const ax = dx !== 0;
  const reach = rng.int(2, 6);
  const pocket = rng.chance(0.65);

  // Everything the crawl touches, plus a cell of rock on both sides and at the
  // far end, measured from one cell past the source so the tube or bay it
  // branches off is not part of the test.
  const depth = pocket ? reach + 4 : reach + 1;
  const half = pocket ? 2 : 1;
  const test = frame(sx, sy, dx, dy, ax, 1, depth, half);
  // Inside the outer wall as well as inside rock. `fillRect` clamps to the grid,
  // so a crawl aimed at the edge of the map would otherwise carve the shell the
  // whole level is sealed by away and leave a room open onto nothing.
  if (!within(grid, test) || !isRock(grid, test)) return false;

  fillRect(grid, frame(sx, sy, dx, dy, ax, 1, reach, 0), Cell.Doorway);
  if (pocket) {
    const bubble = frame(sx, sy, dx, dy, ax, reach + 1, reach + 3, 1);
    fillRect(grid, bubble, Cell.Floor);
    addRegion(site, bubble, 'closet');
    return true;
  }
  // No room for a pocket, so the last cell of the run is the pocket. It has to
  // be `Cell.Floor`: the supply pass will not put anything in a doorway.
  const end = frame(sx, sy, dx, dy, ax, reach, reach, 0);
  fillRect(grid, end, Cell.Floor);
  addRegion(site, end, 'closet');
  return true;
}

/**
 * A rect `t0..t1` cells out from `(sx, sy)` along `(dx, dy)` and `half` cells to
 * each side of the line.
 */
function frame(
  sx: number,
  sy: number,
  dx: number,
  dy: number,
  alongX: boolean,
  t0: number,
  t1: number,
  half: number
): Rect {
  const a = alongX ? sx : sy;
  const step = alongX ? dx : dy;
  const near = a + step * (step > 0 ? t0 : t1);
  const len = t1 - t0 + 1;
  const across = (alongX ? sy : sx) - half;
  const width = half * 2 + 1;
  return alongX
    ? { x: near, y: across, w: len, h: width }
    : { x: across, y: near, w: width, h: len };
}

/* ------------------------------------------------------------------ *
 * Shared tests
 * ------------------------------------------------------------------ */

/** True when every cell of `r` is still the plain floor this pass carved. */
function allFloor(grid: Grid, r: Rect): boolean {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (grid.at(x, y) !== Cell.Floor) return false;
    }
  }
  return true;
}

/** True when `r` lies wholly inside the one-cell outer wall. */
function within(grid: Grid, r: Rect): boolean {
  return r.x >= 1 && r.y >= 1 && r.x + r.w <= grid.w - 1 && r.y + r.h <= grid.h - 1;
}

/** True when nothing has been carved out of `r` yet. Out of bounds counts as rock. */
function isRock(grid: Grid, r: Rect): boolean {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const c = grid.at(x, y);
      if (c !== Cell.Wall && c !== Cell.Void) return false;
    }
  }
  return true;
}

/* ------------------------------------------------------------------ *
 * The fit-out
 * ------------------------------------------------------------------ */

/**
 * Fill the bays.
 *
 * The plan above builds a soft-play centre's *rooms*; this builds its
 * *equipment*, and on this level that is the same job. A Funrooms bay is thirty
 * to seventy metres across, and the general placer in `props.ts` draws pockets
 * of scenery against walls — which on a level of octagonal halls means a
 * climbing frame standing alone in the middle of a blue carpet with nothing
 * else in sight. Walking into one should instead feel like walking *inside* a
 * play frame at the scale of a building.
 *
 * So this pass lays a fit-out rather than scattering objects:
 *
 *  - a **frame** of padded columns on the cell grid, each carrying a deck at
 *    2.25 m, so a block of them is one climbing frame with a continuous first
 *    floor, netting round its edges, tube runs across it and canopies over it;
 *  - a **ball field** laid in two-cell tiles, thinning at the boundary into
 *    loose balls that have rolled out over the carpet;
 *  - **wall lining** — quilted crash padding at dado height with net screens
 *    above it — along every metre of bay wall;
 *  - and the loose kit of a children's venue in between.
 *
 * ### Why any of this is safe
 *
 * A hall filled with solid props is a hall with no way through it, and the
 * reachability gate is the one thing here that cannot be traded away. Every
 * decision below is downstream of that, and the fill is passable **by
 * construction** rather than by a check that happened to pass:
 *
 *  - the ball field carries no footprint at all, so it is a field the player
 *    wades into rather than a wall with balls on it;
 *  - the frame touches the floor only at its columns, and a column is a 0.34 m
 *    box on a 3.4 m pitch. Grown by the player's radius those blocked discs are
 *    0.66 m across, disjoint, and a metre clear of every cell boundary — and a
 *    set of disjoint discs strictly inside a region cannot disconnect it;
 *  - the lining and the decking are a wall decal and a slab at 2.11 m, neither
 *    of which collides with anything;
 *  - and the two kinds that really are bulky, the slide and the castle, are the
 *    only ones that have to earn their spot, which they do by needing all eight
 *    neighbouring cells clear.
 *
 * Nothing is placed on a threshold, within a cell of one, on an exit, on a
 * supply cache, or on top of anything the general pass already stood up.
 *
 * `world/gen/generator.ts` calls it once, after `placeProps` — so it can see
 * and avoid everything already standing — and before the slides go in, so the
 * way down is still installed over the top of the finished scenery.
 */
export function dressFunrooms(
  site: GenSite,
  rng: RNG,
  props: PropSpot[],
  exits: readonly ExitPoint[],
  items: readonly ItemSpot[],
  spawn: Vec2
): void {
  const bays = PLANS.get(site.grid);
  if (!bays || bays.length === 0) return;
  const fit = makeFit(site, rng, props);
  reserve(fit, exits, items, spawn);

  const zones = zoneBays(fit, bays);
  for (const bay of bays) pool(fit, zones, bay);
  for (const bay of bays) climber(fit, zones, bay);
  for (const bay of bays) loose(fit, zones, bay);
  for (const bay of bays) lining(fit, bay);
}

/** Metres of slack round a solid prop before this pass will stand beside it. */
const CLEAR = TUNING.player.radius + 0.08;

/** Zone codes. A bay is a Voronoi patchwork of these, one draw per patch. */
const OPEN = 0;
const FRAME = 1;
const POOL = 2;
const SOFT = 3;

/**
 * Frame module variants, as baked by `buildPlayStructure` in
 * `render/propfun.ts`. The renderer picks its buffer with `variant % count`, so
 * these are the module palette and the two files have to agree on the order.
 */
const M_POST = 0;
const M_DECK = 1;
const M_NET = 2;
const M_CORNER = 3;
const M_TUBE = 4;
const M_UPPER = 5;
const M_LADDER = 6;
const M_CANOPY = 7;

/** Ball-field tile variants, as baked by `buildBallPit`. Same contract. */
const B_FULL = 0;
const B_FULL_ALT = 1;
const B_EDGE = 2;
const B_CORNER = 3;
const B_STRIP = 4;
const B_SPILL = 5;
const B_TRAIL = 6;
const B_DRIFT = 7;

/**
 * Wall-lining rows of the banner atlas: quilted padding, then net screens.
 *
 * Addressed by index because `variantIndex` is `variant % count`, which is the
 * same coupling `props.ts` accepts for the EXIT sign — and for the same reason
 * it is a literal here rather than an import: nothing under `world/` may depend
 * on the renderer. These must match `BANNER_LINING_FROM` and the row order in
 * `render/propfundecals.ts`; there is a note there saying so.
 */
const LINING_ROW = [8, 9] as const;
const SCREEN_ROW = [10, 11] as const;
/** Height of each lining row's centre above the floor, in metres. */
const LINING_Y = [0.6, 0.53] as const;
const SCREEN_Y = [2.05, 2.16] as const;

/** Working state for one level's fit-out. */
interface Fit {
  grid: Grid;
  rng: RNG;
  out: PropSpot[];
  /** 1 where nothing new may put anything solid, or anything at all. */
  busy: Uint8Array;
  /** 1 within a cell of a threshold: the tubes are the only way through. */
  gate: Uint8Array;
  /**
   * 1 on the ground the player must be able to see and stand on: the ways out
   * and the supplies. A subset of `busy` — the part of it that is about the
   * player rather than about not intersecting another model — and the only part
   * a prop with no footprint has to respect.
   */
  keep: Uint8Array;
  /** 1 where the floor already carries dressing, so nothing lands on top of it. */
  dressed: Uint8Array;
}

function makeFit(site: GenSite, rng: RNG, out: PropSpot[]): Fit {
  const grid = site.grid;
  const n = grid.cells.length;
  const fit: Fit = {
    grid,
    rng,
    out,
    busy: new Uint8Array(n),
    gate: new Uint8Array(n),
    keep: new Uint8Array(n),
    dressed: new Uint8Array(n),
  };

  // Thresholds, and everything touching one. A tube is one cell wide and it is
  // the only way between two bays; a ball tile laid over its mouth would be
  // harmless and a padded column in it would end the level.
  for (let y = 1; y < grid.h - 1; y++) {
    for (let x = 1; x < grid.w - 1; x++) {
      if (grid.at(x, y) !== Cell.Doorway) continue;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (grid.inBounds(x + dx, y + dy)) fit.gate[(y + dy) * grid.w + x + dx] = 1;
        }
      }
    }
  }

  // Everything the general pass already stood up, at its real reach.
  for (const spot of out) claim(fit, spot.pos, reachOf(spot));
  return fit;
}

/** World-space reach of a prop's collision box once turned, plus clearance. */
function reachOf(spot: PropSpot): Vec2 {
  const fp = spot.footprint;
  const mount = PROP_DEFS[spot.kind].mount;
  // A wall mark and a ceiling fitting are not in anybody's way, and a decal on
  // the floor is not either. Everything else keeps at least its own cell.
  if (mount === 'wall' || mount === 'ceiling') return { x: 0, z: 0 };
  if (!fp) return { x: 0.1, z: 0.1 };
  const c = Math.abs(Math.cos(spot.yaw));
  const s = Math.abs(Math.sin(spot.yaw));
  return {
    x: (fp.halfX * c + fp.halfZ * s) * spot.scale + CLEAR,
    z: (fp.halfX * s + fp.halfZ * c) * spot.scale + CLEAR,
  };
}

/** Mark every cell whose middle lies inside `reach` of a world point. */
function claim(fit: Fit, pos: Vec2, reach: Vec2, keep = false): void {
  const grid = fit.grid;
  const min = grid.worldToCell(pos.x - reach.x, pos.z - reach.z);
  const max = grid.worldToCell(pos.x + reach.x, pos.z + reach.z);
  for (let cy = min.cy; cy <= max.cy; cy++) {
    for (let cx = min.cx; cx <= max.cx; cx++) {
      if (!grid.inBounds(cx, cy)) continue;
      const c = grid.cellToWorld(cx, cy);
      if (Math.abs(c.x - pos.x) > reach.x || Math.abs(c.z - pos.z) > reach.z) continue;
      fit.busy[cy * grid.w + cx] = 1;
      if (keep) fit.keep[cy * grid.w + cx] = 1;
    }
  }
}

/**
 * The ground the player has to be able to stand on: the ways out and the
 * supplies, each with a ring round it. This is the one rule the pass may never
 * bend, so it is applied to everything — a ball tile over a granola bar is a
 * bar nobody finds, not merely one nobody can walk to.
 */
function reserve(
  fit: Fit,
  exits: readonly ExitPoint[],
  items: readonly ItemSpot[],
  spawn: Vec2
): void {
  for (const exit of exits) {
    claim(fit, exit.pos, { x: exit.radius + 1.4, z: exit.radius + 1.4 }, true);
  }
  for (const item of items) claim(fit, item.pos, { x: 1.2, z: 1.2 }, true);
  // The spawn, which is the one point on the level the player is certain to
  // stand on. Nothing solid within a cell and a half of it: at one module per
  // cell a frame patch that happened to cover the arrival point put a padded
  // column exactly on it, and the run began wedged inside a post.
  //
  // `keep` is deliberately not set. That flag is for ground the player has to be
  // able to *see* — a supply under a heap of balls is a supply nobody finds —
  // and the spawn is not that. Arriving waist deep in a ball pool is a good way
  // to arrive on this level.
  claim(fit, spawn, { x: 2.2, z: 2.2 });
}

/* ------------------------------------------------------------------ *
 * Zoning
 * ------------------------------------------------------------------ */

/**
 * Break every bay into patches of one activity each.
 *
 * A real soft-play centre is laid out in zones — the frame, the ball pool, the
 * toddler mats, the party tables — and it is that, rather than any amount of
 * scatter, that stops a filled hall reading as a warehouse of equipment. A
 * handful of Voronoi seeds per bay is enough: the patches come out as blobs
 * with ragged edges, which is what a fit-out laid round a frame looks like.
 *
 * Each bay draws a character first, so one hall is mostly climbing frame and
 * the next is mostly ball pool. Walking into the second one and finding it
 * different from the first is the entire reason the level has more than one.
 */
function zoneBays(fit: Fit, bays: readonly Bay[]): Uint8Array {
  const grid = fit.grid;
  const zones = new Uint8Array(grid.cells.length);
  for (const bay of bays) {
    const r = bay.rect;
    const area = r.w * r.h;
    const roll = fit.rng.next();
    // Frame-led, pool-led, or a hall laid out as both.
    const table: readonly number[] =
      roll < 0.36
        ? [FRAME, FRAME, FRAME, POOL, SOFT, OPEN]
        : roll < 0.68
          ? [POOL, POOL, POOL, FRAME, SOFT, OPEN]
          : [FRAME, FRAME, POOL, POOL, SOFT, OPEN];

    const count = clamp(Math.round(area / 20), 4, 30);
    const seeds: Array<{ x: number; y: number; z: number }> = [];
    for (let i = 0; i < count; i++) {
      seeds.push({
        x: r.x + fit.rng.int(0, r.w - 1),
        y: r.y + fit.rng.int(0, r.h - 1),
        z: fit.rng.pick(table),
      });
    }
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) {
        if (!grid.inBounds(x, y) || grid.at(x, y) !== Cell.Floor) continue;
        let best = OPEN;
        let bestD = Infinity;
        for (const s of seeds) {
          const d = (s.x - x) * (s.x - x) + (s.y - y) * (s.y - y);
          if (d < bestD) {
            bestD = d;
            best = s.z;
          }
        }
        zones[y * grid.w + x] = best;
      }
    }
  }
  return zones;
}

/* ------------------------------------------------------------------ *
 * Emitting
 * ------------------------------------------------------------------ */

/** Yaw that points a prop's local -Z along a world direction. */
function facing(dx: number, dz: number): number {
  return Math.atan2(dx, -dz);
}

/** The four grid directions, in the order this pass indexes them. */
const NDX = [1, -1, 0, 0] as const;
const NDZ = [0, 0, 1, -1] as const;

interface PutOpts {
  yaw?: number;
  variant?: number;
  scale?: number;
  height?: number;
  /** Metres off the cell centre, in world space. */
  dx?: number;
  dz?: number;
}

/** Stand one prop on one cell. The caller owns the decision that it fits. */
function put(fit: Fit, kind: PropKind, cx: number, cy: number, o: PutOpts = {}): PropSpot {
  const def = PROP_DEFS[kind];
  const base = fit.grid.cellToWorld(cx, cy);
  const spot: PropSpot = {
    id: 0,
    pos: { x: base.x + (o.dx ?? 0), z: base.z + (o.dz ?? 0) },
    yaw: o.yaw ?? 0,
    kind,
    height: o.height ?? 0,
    scale: o.scale ?? fit.rng.range(def.scale[0], def.scale[1]),
    variant: o.variant ?? fit.rng.int(0, 0xffff),
    ...(def.footprint ? { footprint: def.footprint } : {}),
  };
  fit.out.push(spot);
  return spot;
}

/**
 * Can this pass stand something on this cell at all?
 *
 * `solid` is the difference between a ball tile and a padded column: the field
 * is allowed anywhere the player is allowed, because they walk through it, and
 * a column is not allowed anywhere the floor is only one cell wide.
 */
function usable(fit: Fit, cx: number, cy: number, solid: boolean): boolean {
  const grid = fit.grid;
  if (!grid.inBounds(cx, cy)) return false;
  const i = cy * grid.w + cx;
  if (grid.cells[i] !== Cell.Floor) return false;
  if (fit.busy[i] || fit.gate[i]) return false;
  if (!solid) return true;
  return standable(fit, cx, cy);
}

/**
 * Can a field with no footprint be laid over this cell?
 *
 * Deliberately laxer than `usable`. A ball tile is not in anybody's way, so the
 * only things it has to respect are the floor itself, the thresholds, and the
 * ground reserved for the exits and the supplies. In particular it does *not*
 * have to avoid a prop that is already standing: `busy` means "another model is
 * here", and balls lying round the feet of a party table is what a ball pool
 * next to a party table looks like. Testing `busy` instead — which is what this
 * pass used to do — chewed every pool into ridges and islands with bare carpet
 * between them, because a hall this dressed has something standing every few
 * metres, and a ball pool with holes in it is not a ball pool.
 */
function wettable(fit: Fit, cx: number, cy: number): boolean {
  const grid = fit.grid;
  if (!grid.inBounds(cx, cy)) return false;
  const i = cy * grid.w + cx;
  return grid.cells[i] === Cell.Floor && !fit.gate[i] && !fit.keep[i];
}

/** Four walkable neighbours: what a cell needs before anything solid stands. */
function standable(fit: Fit, cx: number, cy: number): boolean {
  const grid = fit.grid;
  // Four walkable neighbours: the column pitch only guarantees a way past if
  // there is floor on every side of the post to walk on.
  for (let d = 0; d < 4; d++) if (!grid.isWalkable(cx + NDX[d], cy + NDZ[d])) return false;
  return true;
}

/**
 * True when all eight neighbours are floor this pass has not spoken for. This
 * is what the two long props — the slide and the castle — have to satisfy, and
 * it is the reason neither of them can be the thing that seals a bay.
 *
 * `against` exempts one side, for the slide backed onto a climbing frame: the
 * frame is what it is supposed to be touching, so the module's own cell and the
 * two beside it are allowed to be taken.
 */
function roomFor(fit: Fit, cx: number, cy: number, against = -1): boolean {
  if (!usable(fit, cx, cy, true)) return false;
  const grid = fit.grid;
  const ax = against >= 0 ? NDX[against] : 0;
  const az = against >= 0 ? NDZ[against] : 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (!grid.isWalkable(cx + dx, cy + dy)) return false;
      if (against >= 0 && dx * ax + dy * az > 0) continue;
      if (fit.busy[(cy + dy) * grid.w + cx + dx]) return false;
    }
  }
  return true;
}

function take(fit: Fit, cx: number, cy: number, radius = 0): void {
  const grid = fit.grid;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (grid.inBounds(cx + dx, cy + dy)) fit.busy[(cy + dy) * grid.w + cx + dx] = 1;
    }
  }
}

/* ------------------------------------------------------------------ *
 * The ball field
 * ------------------------------------------------------------------ */

/**
 * Lay the ball pool.
 *
 * Two cells to a tile, laid edge to edge, so a pool is as big as its patch of
 * the bay wants to be rather than as big as one object — which is the whole
 * difference between "there is a ball pit in this room" and "the floor of this
 * end of the hall is balls". A tile's variant is chosen from which of its
 * neighbours are also pool, so the middle is heaped and the rim thins out; and
 * the blocks *outside* the rim get a scatter of balls that have rolled clear,
 * which is what stops the pool having a straight edge anywhere.
 *
 * None of this is in the way. The kind carries no footprint at all, so the
 * player walks in, and it can therefore be laid over any floor the plan will
 * let a player stand on.
 */
function pool(fit: Fit, zones: Uint8Array, bay: Bay): void {
  const grid = fit.grid;
  const r = bay.rect;
  // A tile is anchored on a two-by-two block, so its middle is the corner
  // between the four cells rather than any cell's centre.
  const half = grid.cellSize * 0.5;
  const cols = r.w >> 1;
  const rows = r.h >> 1;
  if (cols < 1 || rows < 1) return;

  // Which two-by-two blocks are pool, decided before any of them is laid: a
  // tile's variant depends on its neighbours, so the shape of the pool has to
  // exist before the first tile does.
  // Whether this hall's climbing frame stands in the water.
  //
  // The ground floor of a soft-play frame is a ball pool about as often as it is
  // carpet, and it is the single most recognisable thing in the building: posts
  // rising out of a field of balls, decking overhead, an aisle between two
  // blocks that you wade down. It also costs nothing — a ball tile carries no
  // footprint, so the pool runs straight under the frame and round every column
  // without changing what the player can walk through by one centimetre. Rolled
  // per bay rather than per patch so that a hall is either flooded or dry, which
  // is what makes walking into the next one worth doing.
  const flooded = fit.rng.chance(0.55);

  const wet = new Uint8Array(cols * rows);
  const free = new Uint8Array(cols * rows);
  for (let by = 0; by < rows; by++) {
    for (let bx = 0; bx < cols; bx++) {
      let ok = true;
      let n = 0;
      for (let j = 0; j < 2 && ok; j++) {
        for (let i = 0; i < 2; i++) {
          const x = r.x + bx * 2 + i;
          const y = r.y + by * 2 + j;
          if (!wettable(fit, x, y)) {
            ok = false;
            break;
          }
          const z = zones[y * grid.w + x];
          if (z === POOL || (flooded && z === FRAME)) n++;
        }
      }
      free[by * cols + bx] = ok ? 1 : 0;
      wet[by * cols + bx] = ok && n >= 3 ? 1 : 0;
    }
  }

  const at = (bx: number, by: number): number =>
    bx < 0 || by < 0 || bx >= cols || by >= rows ? 0 : wet[by * cols + bx]!;

  for (let by = 0; by < rows; by++) {
    for (let bx = 0; bx < cols; bx++) {
      const centreX = r.x + bx * 2;
      const centreY = r.y + by * 2;
      const laid = wet[by * cols + bx] === 1;

      if (!laid) {
        // Balls that got out. Only beside a pool, and only sometimes: a trail
        // of them everywhere is litter rather than a boundary.
        if (free[by * cols + bx] !== 1) continue;
        let touches = -1;
        for (let d = 0; d < 4; d++) if (at(bx + NDX[d], by + NDZ[d])) touches = d;
        // Two blocks out rather than one, and more often. Loose balls tracked
        // out across the carpet are the cheapest floor covering on the level —
        // no footprint, no reachability cost, nothing to walk round — and they
        // are what stops the open ground between a pool and a frame reading as
        // an empty blue hall with equipment round the edges.
        if (touches < 0) {
          for (let d = 0; d < 4 && touches < 0; d++) {
            if (at(bx + NDX[d] * 2, by + NDZ[d] * 2)) touches = d;
          }
          if (touches < 0 || !fit.rng.chance(0.3)) continue;
        } else if (!fit.rng.chance(0.62)) continue;
        const trail = fit.rng.chance(0.45);
        put(fit, 'ball-pit', centreX, centreY, {
          dx: half,
          dz: half,
          scale: 1,
          variant: trail ? B_TRAIL : B_SPILL,
          // A trail is thickest at one corner and thins to nothing across the
          // diagonal, so it is turned to run away from the pool it came out of.
          // The loose scatter is even and its facing does not matter.
          yaw: trail ? cornerYaw(-NDX[touches], -NDZ[touches]) : 0,
        });
        continue;
      }

      const missing: number[] = [];
      for (let d = 0; d < 4; d++) if (!at(bx + NDX[d], by + NDZ[d])) missing.push(d);
      let variant = B_FULL;
      let yaw = 0;
      if (missing.length === 0) {
        variant = fit.rng.chance(0.5) ? B_FULL : B_FULL_ALT;
        yaw = (Math.PI / 2) * fit.rng.int(0, 3);
      } else if (missing.length === 1) {
        const d = missing[0]!;
        variant = B_EDGE;
        // The thin end is the tile's local +Z, so it is turned to point out of
        // the pool: the rim runs down to loose balls exactly where it stops.
        yaw = facing(-NDX[d], -NDZ[d]);
      } else if (missing.length === 2 && opposite(missing[0]!, missing[1]!)) {
        variant = B_STRIP;
        yaw = facing(-NDX[missing[0]!], -NDZ[missing[0]!]);
      } else if (missing.length === 2) {
        variant = B_CORNER;
        yaw = cornerYaw(NDX[missing[0]!] + NDX[missing[1]!], NDZ[missing[0]!] + NDZ[missing[1]!]);
      } else {
        variant = B_DRIFT;
        const d = missing.length === 3 ? nonMissing(missing) : 0;
        yaw = facing(-NDX[d], -NDZ[d]);
      }

      put(fit, 'ball-pit', centreX, centreY, { dx: half, dz: half, scale: 1, variant, yaw });
      for (let j = 0; j < 2; j++) {
        for (let i = 0; i < 2; i++) {
          const idx = (centreY + j) * grid.w + centreX + i;
          if (grid.inBounds(centreX + i, centreY + j)) fit.dressed[idx] = 1;
        }
      }
    }
  }
}

/**
 * Yaw that points a tile's thin corner — its local (+X, +Z) — along a world
 * direction. That diagonal sits a quarter turn round from local +X, hence the
 * offset.
 */
function cornerYaw(dx: number, dz: number): number {
  return Math.atan2(dz, dx) - Math.PI / 4;
}

function opposite(a: number, b: number): boolean {
  return (a >> 1) === (b >> 1);
}

/** The one direction not in a list of three. */
function nonMissing(missing: readonly number[]): number {
  for (let d = 0; d < 4; d++) if (!missing.includes(d)) return d;
  return 0;
}

/* ------------------------------------------------------------------ *
 * The frame
 * ------------------------------------------------------------------ */

/**
 * Build the climbing frame over a bay's frame patches.
 *
 * One module per cell, so a patch of eight cells is one frame twenty-seven
 * metres across with a first floor over all of it. What each module carries is
 * decided by where it sits in the block: a module with frame on every side is
 * interior and gets a deck with something on top of it, a module with one side
 * open gets the netting along that side, a corner gets two runs of it, and a
 * module with almost nothing next to it is not a frame at all — it is a single
 * padded post, which is what the last one before the open floor should be.
 *
 * The tube runs are laid as whole lines rather than drawn per module. A single
 * 3.4 m section of tube over a deck is a section of tube; six in a row with
 * their collars meeting is a tunnel across the frame, and a tunnel across the
 * frame is the thing an eleven-year-old is actually looking at.
 */
function climber(fit: Fit, zones: Uint8Array, bay: Bay): void {
  const grid = fit.grid;
  const r = bay.rect;
  const mask = new Uint8Array(r.w * r.h);
  const inBay = (x: number, y: number): boolean =>
    x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
  const marked = (x: number, y: number): boolean =>
    inBay(x, y) && mask[(y - r.y) * r.w + (x - r.x)] === 1;

  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (zones[y * grid.w + x] !== FRAME) continue;
      if (!usable(fit, x, y, true)) continue;
      // A few gaps, because a frame with no holes in it is a warehouse rack.
      if (fit.rng.chance(0.07)) continue;
      mask[(y - r.y) * r.w + (x - r.x)] = 1;
    }
  }
  aisles(fit, r, mask);

  // Up to two tube runs, each a whole row or column of the bay.
  const tubes = new Uint8Array(r.w * r.h);
  const tubeYaw = new Float32Array(r.w * r.h);
  for (let t = 0; t < 2; t++) {
    if (!fit.rng.chance(t === 0 ? 0.7 : 0.35)) continue;
    const alongX = fit.rng.chance(0.5);
    if (alongX) {
      const y = r.y + fit.rng.int(1, Math.max(1, r.h - 2));
      for (let x = r.x; x < r.x + r.w; x++) {
        if (!marked(x, y)) continue;
        tubes[(y - r.y) * r.w + (x - r.x)] = 1;
        tubeYaw[(y - r.y) * r.w + (x - r.x)] = 0;
      }
    } else {
      const x = r.x + fit.rng.int(1, Math.max(1, r.w - 2));
      for (let y = r.y; y < r.y + r.h; y++) {
        if (!marked(x, y)) continue;
        tubes[(y - r.y) * r.w + (x - r.x)] = 1;
        tubeYaw[(y - r.y) * r.w + (x - r.x)] = Math.PI / 2;
      }
    }
  }

  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const k = (y - r.y) * r.w + (x - r.x);
      if (mask[k] !== 1) continue;

      const open: number[] = [];
      for (let d = 0; d < 4; d++) if (!marked(x + NDX[d], y + NDZ[d])) open.push(d);

      let variant: number;
      let yaw = (Math.PI / 2) * fit.rng.int(0, 3);
      if (open.length >= 3) {
        // Nothing to carry a deck to. A post, and the frame stops here.
        variant = M_POST;
      } else if (tubes[k] === 1) {
        variant = M_TUBE;
        yaw = tubeYaw[k]!;
      } else if (open.length === 2 && !opposite(open[0]!, open[1]!)) {
        const d1 = open[0]!;
        const d2 = open[1]!;
        // The module's two netted faces are its local -Z and local -X, and local
        // -X sits a quarter turn clockwise of local -Z. Whichever of the two
        // open sides is the clockwise one has to be the -X one.
        const lead = NDX[d2] === NDZ[d1] && NDZ[d2] === -NDX[d1] ? d1 : d2;
        variant = M_CORNER;
        yaw = facing(NDX[lead], NDZ[lead]);
      } else if (open.length >= 1) {
        const d = open[0]!;
        variant = M_NET;
        yaw = facing(NDX[d], NDZ[d]);
      } else {
        variant = fit.rng.pick(INSIDE_MODULES);
      }

      put(fit, 'play-structure', x, y, { yaw, variant, scale: 1 });
      take(fit, x, y);
      fit.dressed[y * grid.w + x] = 1;
    }
  }

  // The ways down. A first floor with nothing coming off it is a mezzanine, so
  // a frame gets one or two slides backed onto its edge — placed here rather
  // than left to the loose pass, which would only ever find one by accident.
  const edges: number[] = [];
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (marked(x, y)) continue;
      let touches = -1;
      for (let d = 0; d < 4; d++) if (marked(x + NDX[d], y + NDZ[d])) touches = d;
      if (touches >= 0 && roomFor(fit, x, y, touches)) edges.push((y * grid.w + x) * 4 + touches);
    }
  }
  let wanted = Math.min(edges.length, fit.rng.int(1, 2));
  for (let n = 0; n < 12 && wanted > 0; n++) {
    const packed = edges[fit.rng.int(0, edges.length - 1)]!;
    const cell = (packed / 4) | 0;
    const d = packed % 4;
    const x = cell % grid.w;
    const y = (cell / grid.w) | 0;
    if (!roomFor(fit, x, y, d)) continue;
    // Backed onto the frame, so the platform lands at the deck edge and the
    // chute runs out across the open floor. The platform end of the box does
    // overlap the module's column, which is a metre and a half away — the two
    // solids interpenetrate, and that is harmless: the model's legs pass either
    // side of the post, and two overlapping obstacles neither open a route nor
    // close one.
    put(fit, 'play-slide', x, y, { yaw: facing(-NDX[d], -NDZ[d]) });
    take(fit, x, y, 1);
    fit.dressed[cell] = 1;
    wanted--;
  }
}

/**
 * Cut a frame patch into blocks, by clearing whole rows and columns of it.
 *
 * This is the difference between a climbing frame and a car park, and it is
 * worth being blunt about why. Everything that makes a module legible as play
 * equipment — the netting, the rails, the tube runs, the mezzanines, the
 * canopies — sits at or above the deck at 2.25 m, and the player's eye is at
 * 1.68 m. Standing under a frame you cannot see any of it. All you get is the
 * padded column and the flat soffit overhead, so a zoning patch that ran the
 * width of a bay came out as a hall roofed end to end in green: a room with a
 * low ceiling on posts, with the entire fit-out hidden on top of it.
 *
 * A block four to six modules on a side is a frame you can stand outside and
 * see whole — decking at first-floor height, nets and tunnels above it, real
 * ceiling and real light behind it — and an aisle between two of them is a
 * street with the flank of a climbing frame down each side. It also roughly
 * doubles the share of modules that are on an edge, and an edge module is the
 * only kind that carries netting, a rail and a fascia at all.
 */
function aisles(fit: Fit, r: Rect, mask: Uint8Array): void {
  const clearCol = (x: number): void => {
    for (let y = r.y; y < r.y + r.h; y++) mask[(y - r.y) * r.w + (x - r.x)] = 0;
  };
  const clearRow = (y: number): void => {
    for (let x = r.x; x < r.x + r.w; x++) mask[(y - r.y) * r.w + (x - r.x)] = 0;
  };
  // The first aisle lands anywhere in the first block-and-a-bit, so two bays
  // never cut in the same place, and each gap after it is drawn again.
  for (let x = r.x + fit.rng.int(3, 6); x < r.x + r.w; x += fit.rng.int(5, 7)) clearCol(x);
  for (let y = r.y + fit.rng.int(3, 6); y < r.y + r.h; y += fit.rng.int(5, 7)) clearRow(y);
}

/**
 * What a module in the middle of a frame carries. Weighted by repetition, which
 * is how the rest of the catalogue does it: mostly plain deck, because a first
 * floor where every bay has a canopy over it has no first floor left to see.
 */
const INSIDE_MODULES: readonly number[] = [
  M_DECK,
  M_DECK,
  M_DECK,
  M_DECK,
  M_UPPER,
  M_UPPER,
  M_LADDER,
  M_LADDER,
  M_CANOPY,
  M_NET,
];

/* ------------------------------------------------------------------ *
 * Everything else on the floor
 * ------------------------------------------------------------------ */

/**
 * The loose kit of a children's venue: foam shapes, balloons, tables laid for a
 * party, a tunnel section nobody put up, a castle that went down with its blower
 * still running, and a slide standing on its own.
 *
 * This is the pass that fills the parts of a bay the frame and the pool do not,
 * and its rate is deliberately much higher than the general placer's — the
 * point of the level is that the equipment *is* the content, so a stretch of
 * bare carpet here is a mistake rather than breathing room. What keeps it from
 * reading as a jumble is the zoning: this only ever runs on a patch the zoning
 * called soft play or left open, so the mats are in one place and the tables in
 * another.
 */
function loose(fit: Fit, zones: Uint8Array, bay: Bay): void {
  const grid = fit.grid;
  const r = bay.rect;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const i = y * grid.w + x;
      if (fit.dressed[i]) continue;
      const zone = zones[i]!;
      const soft = zone === SOFT;
      if (zone === POOL || zone === FRAME) {
        // Under the frame and round the edge of the pool. Foam only: anything
        // with legs standing in a ball pool is something somebody dumped there.
        if (usable(fit, x, y, true) && fit.rng.chance(0.24)) blocks(fit, x, y);
        continue;
      }
      const roll = fit.rng.next();
      const rate = soft ? 1 : 0.66;
      if (roll > 0.86 * rate) continue;

      if (roll < 0.3 * rate) {
        if (usable(fit, x, y, true)) blocks(fit, x, y);
      } else if (roll < 0.42 * rate) {
        if (usable(fit, x, y, false)) {
          put(fit, 'confetti', x, y, { yaw: fit.rng.range(-Math.PI, Math.PI) });
          fit.dressed[i] = 1;
        }
      } else if (roll < 0.54 * rate) {
        if (usable(fit, x, y, true)) {
          put(fit, 'balloons', x, y, { yaw: fit.rng.range(-Math.PI, Math.PI) });
          take(fit, x, y);
          fit.dressed[i] = 1;
        }
      } else if (roll < 0.66 * rate) {
        if (usable(fit, x, y, true)) {
          put(fit, 'crawl-tube', x, y, { yaw: (Math.PI / 2) * fit.rng.int(0, 3) });
          take(fit, x, y);
          fit.dressed[i] = 1;
        }
      } else if (roll < 0.78 * rate) {
        if (usable(fit, x, y, true)) {
          put(fit, 'party-table', x, y, { yaw: (Math.PI / 2) * fit.rng.int(0, 3) });
          take(fit, x, y);
          fit.dressed[i] = 1;
        }
      } else if (roll < 0.83 * rate) {
        // The two bulky ones. All eight neighbours have to be clear floor,
        // which in a bay is common and in a walkway or a chamfered corner is
        // not — and those are exactly the places one would be in the way.
        if (roomFor(fit, x, y)) {
          // A slide's platform is at deck height, so where there is a frame
          // next door it is turned to back onto it: the chute then reads as the
          // way down off the first floor rather than as a slide that happens to
          // be standing beside one. Everywhere else it faces the open floor.
          const toFrame = frameSide(fit, zones, x, y);
          const yaw =
            toFrame >= 0
              ? facing(-NDX[toFrame], -NDZ[toFrame])
              : (Math.PI / 2) * fit.rng.int(0, 3);
          put(fit, 'play-slide', x, y, { yaw });
          take(fit, x, y, 1);
          fit.dressed[i] = 1;
        }
      } else if (roomFor(fit, x, y)) {
        put(fit, 'bouncy-castle', x, y, { yaw: (Math.PI / 2) * fit.rng.int(0, 3) });
        take(fit, x, y, 1);
        fit.dressed[i] = 1;
      }
    }
  }
}

/**
 * Which side of a cell the climbing frame is on, or -1. A module's own cell is
 * marked dressed by the frame pass, so a frame neighbour is a cell that is both
 * in a frame patch and has something standing on it.
 */
function frameSide(fit: Fit, zones: Uint8Array, cx: number, cy: number): number {
  const grid = fit.grid;
  for (let d = 0; d < 4; d++) {
    const x = cx + NDX[d];
    const y = cy + NDZ[d];
    if (!grid.inBounds(x, y)) continue;
    const i = y * grid.w + x;
    if (zones[i] === FRAME && fit.dressed[i]) return d;
  }
  return -1;
}

/** A stack of foam shapes, and one or two more beside it. */
function blocks(fit: Fit, cx: number, cy: number): void {
  const grid = fit.grid;
  const run = fit.rng.int(1, 3);
  const d = fit.rng.int(0, 3);
  for (let k = 0; k < run; k++) {
    const x = cx + NDX[d] * k;
    const y = cy + NDZ[d] * k;
    if (!usable(fit, x, y, true)) break;
    put(fit, 'soft-blocks', x, y, { yaw: fit.rng.range(-Math.PI, Math.PI) });
    take(fit, x, y);
    fit.dressed[y * grid.w + x] = 1;
  }
}

/* ------------------------------------------------------------------ *
 * The walls
 * ------------------------------------------------------------------ */

/**
 * Line the bay walls.
 *
 * There is no bare wall in a soft-play centre: the bottom metre is quilted
 * crash padding and above it is net screening, and between them they are half
 * of why the inside of one looks the way it does. Both are rows of the banner
 * atlas — see `render/propfundecals.ts` — so they are wall decals, which means
 * a bay can be lined from end to end for nothing at all in collision.
 *
 * Panels are drawn a little wider than a cell so they overlap rather than leave
 * a seam, and consecutive cells stand their panel a couple of centimetres
 * further off the wall than their neighbour, so two overlapping panels are
 * never coplanar and never fight for the same depth.
 */
function lining(fit: Fit, bay: Bay): void {
  const grid = fit.grid;
  const r = bay.rect;
  const half = grid.cellSize * 0.5;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const i = y * grid.w + x;
      if (grid.cells[i] !== Cell.Floor || fit.gate[i]) continue;
      for (let d = 0; d < 4; d++) {
        if (!grid.isSolid(x + NDX[d], y + NDZ[d])) continue;
        if (!fit.rng.chance(0.82)) continue;
        const stagger = (x + y) & 1;
        const back = half - 0.08 - stagger * 0.02;
        const row = fit.rng.int(0, 1);
        // Facing out of the wall, the same convention every other wall mount on
        // the level uses.
        const yaw = facing(-NDX[d], -NDZ[d]);
        put(fit, 'banner', x, y, {
          yaw,
          scale: 1,
          variant: LINING_ROW[row]!,
          height: LINING_Y[row]!,
          dx: NDX[d] * back,
          dz: NDZ[d] * back,
        });
        if (!fit.rng.chance(0.42)) continue;
        // Above the padding: net screening nearly always, and once in a while
        // one of the venue's banners instead. The banners are the only thing on
        // these walls that says anybody was expected — they have to stay rare
        // enough that finding one is still finding something.
        if (fit.rng.chance(0.06)) {
          put(fit, 'banner', x, y, {
            yaw,
            variant: fit.rng.int(0, LINING_ROW[0]! - 1),
            height: 2.32,
            dx: NDX[d] * (back - 0.05),
            dz: NDZ[d] * (back - 0.05),
          });
          continue;
        }
        const screen = fit.rng.int(0, 1);
        put(fit, 'banner', x, y, {
          yaw,
          scale: 1,
          variant: SCREEN_ROW[screen]!,
          height: SCREEN_Y[screen]!,
          dx: NDX[d] * (back - 0.015),
          dz: NDZ[d] * (back - 0.015),
        });
      }
    }
  }
}
